import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, Send, FileText, Briefcase } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";

const PDF_KINDS = {
  relatorio: { label: "Relatório completo", icon: FileText, path: "relatorio" },
  proposta: { label: "Proposta Comercial", icon: Briefcase, path: "proposta" },
};

/**
 * Modal para enviar Relatório e/ou Proposta por e-mail.
 * Renderiza iframes ocultos com `?embed=1&autopdf=1` para gerar os PDFs via html2pdf
 * dentro de cada rota; recebe os blobs via postMessage e envia ao backend em
 * uma requisição multipart/form-data.
 */
const SendEmailDialog = ({ diagnosticId, defaultEmail = "", company = "" }) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(defaultEmail || "");
  const [message, setMessage] = useState("");
  const [pick, setPick] = useState({ relatorio: true, proposta: true });
  const [sending, setSending] = useState(false);
  const [progressMsg, setProgressMsg] = useState("");
  const [iframes, setIframes] = useState({ relatorio: false, proposta: false });
  const blobsRef = useRef({});
  const resolversRef = useRef({});

  useEffect(() => {
    setEmail(defaultEmail || "");
  }, [defaultEmail, open]);

  // Listen for postMessage from iframes
  useEffect(() => {
    const onMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data || {};
      if (data.type === "pdf-ready" && data.kind) {
        const resolver = resolversRef.current[data.kind];
        if (resolver) {
          blobsRef.current[data.kind] = data.blob;
          resolver.resolve(data.blob);
        }
      } else if (data.type === "pdf-error" && data.kind) {
        const resolver = resolversRef.current[data.kind];
        if (resolver) resolver.reject(new Error(data.error || "Falha na geração do PDF"));
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const awaitBlob = (kind, timeoutMs = 25000) =>
    new Promise((resolve, reject) => {
      resolversRef.current[kind] = { resolve, reject };
      const timer = setTimeout(() => {
        if (resolversRef.current[kind]) {
          delete resolversRef.current[kind];
          reject(new Error(`Tempo esgotado gerando PDF (${kind})`));
        }
      }, timeoutMs);
      const orig = resolversRef.current[kind];
      resolversRef.current[kind] = {
        resolve: (v) => {
          clearTimeout(timer);
          orig.resolve(v);
        },
        reject: (e) => {
          clearTimeout(timer);
          orig.reject(e);
        },
      };
    });

  const handleSend = useCallback(async () => {
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      toast.error("Informe um e-mail válido.");
      return;
    }
    const selected = Object.keys(pick).filter((k) => pick[k]);
    if (selected.length === 0) {
      toast.error("Selecione pelo menos um documento para envio.");
      return;
    }

    setSending(true);
    blobsRef.current = {};
    try {
      // Mount each iframe to trigger PDF generation
      setProgressMsg("Gerando PDF(s)...");
      setIframes((s) => {
        const next = { ...s };
        selected.forEach((k) => (next[k] = true));
        return next;
      });

      // Await blobs sequentially to limit memory/CPU
      const blobs = {};
      for (const kind of selected) {
        setProgressMsg(`Gerando ${PDF_KINDS[kind].label}...`);
        blobs[kind] = await awaitBlob(kind);
      }

      // Build FormData
      setProgressMsg("Enviando e-mail...");
      const fd = new FormData();
      fd.append("to_email", email);
      if (message?.trim()) fd.append("message", message.trim());
      if (blobs.relatorio) fd.append("relatorio", blobs.relatorio, "relatorio.pdf");
      if (blobs.proposta) fd.append("proposta", blobs.proposta, "proposta.pdf");

      await api.post(`/diagnostics/${diagnosticId}/send-email`, fd);

      toast.success("E-mail enviado com sucesso.");
      setOpen(false);
      setMessage("");
    } catch (err) {
      toast.error(formatApiError(err) || err?.message || "Falha ao enviar e-mail.");
    } finally {
      setSending(false);
      setProgressMsg("");
      setIframes({ relatorio: false, proposta: false });
      resolversRef.current = {};
    }
  }, [diagnosticId, email, message, pick]);

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => !sending && setOpen(v)}>
        <DialogTrigger asChild>
          <Button
            data-testid="open-send-email-btn"
            className="bg-[#22B673] hover:bg-[#1a9b60] text-white"
          >
            <Mail size={14} className="mr-2" /> Enviar por e-mail
          </Button>
        </DialogTrigger>
        <DialogContent
          data-testid="send-email-dialog"
          className="bg-[#1C1F26] border-[#3A3F47] text-white sm:max-w-[520px]"
        >
          <DialogHeader>
            <DialogTitle className="text-white">Enviar por e-mail</DialogTitle>
            <DialogDescription className="text-[#9aa3ad]">
              Geramos os PDFs no navegador e enviamos via SMTP para o destinatário abaixo.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="text-xs text-[#6B8DB3] uppercase tracking-wider">
                Destinatário
              </Label>
              <Input
                type="email"
                data-testid="send-email-recipient"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@empresa.com.br"
                className="bg-[#0e1015] border-[#3A3F47] text-white mt-1 h-11"
                disabled={sending}
              />
              {company && (
                <div className="text-[10px] text-[#6B8DB3] mt-1">
                  Empresa: {company}
                </div>
              )}
            </div>

            <div>
              <Label className="text-xs text-[#6B8DB3] uppercase tracking-wider mb-2 block">
                Documentos
              </Label>
              <div className="space-y-2">
                {Object.entries(PDF_KINDS).map(([key, info]) => {
                  const Icon = info.icon;
                  return (
                    <label
                      key={key}
                      htmlFor={`pdf-${key}`}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        pick[key]
                          ? "border-[#22B673] bg-[#22B673]/5"
                          : "border-[#3A3F47] bg-[#0e1015] hover:border-[#6B8DB3]"
                      }`}
                    >
                      <Checkbox
                        id={`pdf-${key}`}
                        data-testid={`pdf-toggle-${key}`}
                        checked={pick[key]}
                        onCheckedChange={(v) =>
                          setPick((p) => ({ ...p, [key]: Boolean(v) }))
                        }
                        disabled={sending}
                      />
                      <Icon size={16} className="text-[#6B8DB3]" />
                      <span className="text-sm text-white">{info.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div>
              <Label className="text-xs text-[#6B8DB3] uppercase tracking-wider">
                Mensagem (opcional)
              </Label>
              <Textarea
                data-testid="send-email-message"
                rows={3}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Adicione uma mensagem personalizada para o destinatário..."
                className="bg-[#0e1015] border-[#3A3F47] text-white mt-1 resize-none"
                disabled={sending}
              />
            </div>

            {progressMsg && (
              <div className="flex items-center gap-2 text-xs text-[#6B8DB3]">
                <Loader2 size={14} className="animate-spin" /> {progressMsg}
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={() => setOpen(false)}
              disabled={sending}
              className="text-[#9aa3ad] hover:text-white hover:bg-[#0e1015]"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSend}
              disabled={sending || !email}
              data-testid="send-email-confirm-btn"
              className="bg-[#B9262B] hover:bg-[#9a1e22] text-white"
            >
              {sending ? (
                <Loader2 className="animate-spin" size={16} />
              ) : (
                <>
                  <Send size={14} className="mr-2" /> Enviar
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hidden iframes used to generate PDFs */}
      <div
        aria-hidden="true"
        style={{
          position: "fixed",
          left: "-10000px",
          top: 0,
          width: "210mm",
          height: "297mm",
          pointerEvents: "none",
          opacity: 0,
        }}
      >
        {iframes.relatorio && (
          <iframe
            title="pdf-relatorio"
            src={`/admin/diagnosticos/${diagnosticId}/relatorio?embed=1&autopdf=1`}
            style={{ width: "210mm", height: "1200mm", border: 0, background: "#fff" }}
          />
        )}
        {iframes.proposta && (
          <iframe
            title="pdf-proposta"
            src={`/admin/diagnosticos/${diagnosticId}/proposta?embed=1&autopdf=1`}
            style={{ width: "210mm", height: "320mm", border: 0, background: "#fff" }}
          />
        )}
      </div>
    </>
  );
};

export default SendEmailDialog;
